import { defineStore } from 'pinia'
import { ref, computed, unref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import {
  fetchFlowExecutionMetrics,
  fetchBatchFlowExecutionMetrics,
  fetchFlowSummaryMetrics,
  type NodeMetrics,
  type PathFlow,
  type DropOff,
  type FlowExecutionMetricsResponse,
  type FlowSummaryMetrics
} from '../services/flowMetricsService'

export const useFlowMetricsStore = defineStore('flowMetrics', () => {
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const reportStore = useReportStore()
  const { dateRange } = storeToRefs(reportStore)

  // Cache for node metrics by flow ID
  const nodeMetricsCache = ref<Record<string, Record<string, NodeMetrics>>>({})
  
  // Cache for path flow data by flow ID
  const pathFlowCache = ref<Record<string, { paths: PathFlow[], dropOffs: DropOff[] }>>({})

  // Store queries by flow ID to reuse them
  const flowQueries = ref<Record<string, any>>({})

  // Initialize a query for a specific flow (must be called in setup)
  const initFlowMetricsQuery = (flowId: string) => {
    if (!flowQueries.value[flowId]) {
      flowQueries.value[flowId] = useQuery({
        queryKey: computed(() => [
          'flowMetrics',
          currentTeamPid.value,
          flowId,
          dateRange.value
        ]),
        queryFn: async () => {
          const data = await fetchFlowExecutionMetrics(currentTeamPid.value!, {
            flowId,
            start: dateRange.value.start,
            end: dateRange.value.end
          })

          // Update the cache when data is fetched
          if (data.nodeMetrics) {
            nodeMetricsCache.value[flowId] = data.nodeMetrics
          }
          
          // Update path flow cache
          if (data.pathFlows) {
            pathFlowCache.value[flowId] = data.pathFlows
          }

          return data
        },
        enabled: computed(() => !!currentTeamPid.value),
        staleTime: 30000, // 30 seconds
        gcTime: 300000 // 5 minutes (changed from cacheTime which is deprecated)
      })
    }
    return flowQueries.value[flowId]
  }

  // Get metrics for a specific node
  const getNodeMetrics = (
    flowId: string,
    nodeId: string,
    nodeType?: string
  ): NodeMetrics | null => {
    // First check cache - it might have data from the initial load
    const cached = nodeMetricsCache.value[flowId]?.[nodeId]

    // Then check if query has fresher data
    const query = flowQueries.value[flowId]
    const queryData = query?.data?.value?.nodeMetrics?.[nodeId]

    // If we have data, return it
    if (queryData || cached) {
      return queryData || cached
    }
    
    // If no data exists but we know the node type, return default metrics
    // This ensures we always show metrics UI with 0 values
    if (nodeType) {
      const defaultMetrics: NodeMetrics = {
        nodeId,
        nodeType,
        visitorCount: 0,
        conversionCount: 0,
        conversionRate: 0,
        avgDurationMs: 0
      }
      
      // Add type-specific default fields
      if (nodeType === 'yesno') {
        defaultMetrics.branchDistribution = { yes: 0, no: 0 }
      } else if (nodeType === 'sg') {
        defaultMetrics.splitDistribution = {}
      } else if (nodeType === 'random') {
        defaultMetrics.bucketDistribution = {}
      }
      
      return defaultMetrics
    }
    
    return null
  }

  // Format metrics for display in nodes
  const formatNodeMetrics = (metrics: NodeMetrics | null) => {
    if (!metrics) return null

    // Special formatting for question nodes (sg type with asks/completions)
    // Show Asks/Completions for any SG node that has visitor count and conversion data
    // This includes both pure questions and questions with routing (splits)
    if (metrics.nodeType === 'sg' && metrics.visitorCount !== undefined && metrics.conversionCount !== undefined) {
      const asks = metrics.visitorCount
      const completions = metrics.conversionCount || 0
      const answerRate = asks > 0 ? ((completions / asks) * 100).toFixed(2) : '0.00'
      
      const questionMetrics = [
        {
          label: 'Asks',
          value: asks.toLocaleString()
        },
        {
          label: 'Completions',
          value: completions.toLocaleString()
        },
        {
          label: 'Answer Rate',
          value: `${answerRate}%`
        }
      ]
      
      // Always include split distribution for questions
      // The backend should provide answer breakdown for all questions since we track selectedSegmentId
      if (metrics.splitDistribution || metrics.answerDistribution) {
        // Add the Splits metric for SplitNode component to use
        // Use answerDistribution if available (for questions without routing), otherwise splitDistribution
        const distribution = metrics.answerDistribution || metrics.splitDistribution
        questionMetrics.push({
          label: 'Splits',
          value: JSON.stringify(distribution)
        })
      }
      
      return questionMetrics
    }

    // Special formatting for offer nodes to match legacy format
    if (metrics.nodeType === 'offer') {
      const displays = metrics.visitorCount || 0
      const conversions = metrics.conversionCount || 0
      const rate = displays > 0 ? ((conversions / displays) * 100).toFixed(1) : '0.0'
      
      return [
        {
          label: 'Displays',
          value: displays.toLocaleString()
        },
        {
          label: 'Clicks',  // Note: OfferNode will override this to 'Submits' for forms
          value: conversions.toLocaleString()
        },
        {
          label: 'Conversion Rate',
          value: `${rate}%`
        }
      ]
    }

    // Default formatting for other node types
    const baseMetrics = [
      {
        label: 'Visitors',
        value: metrics.visitorCount.toLocaleString()
      }
    ]

    // Add conversion rate if available (skip for pure routing nodes)
    const routingOnlyNodes = ['highscore', 'random']
    if (metrics.conversionRate !== undefined && metrics.conversionCount !== undefined &&
        !routingOnlyNodes.includes(metrics.nodeType)) {
      baseMetrics.push({
        label: 'Conversions',
        value: `${metrics.conversionRate}% (${metrics.conversionCount})`
      })
    }

    // Add avg duration if meaningful (> 1 second)
    if (metrics.avgDurationMs > 1000) {
      baseMetrics.push({
        label: 'Avg Time',
        value: `${(metrics.avgDurationMs / 1000).toFixed(1)}s`
      })
    }

    if (metrics.nodeType === 'yesno' && metrics.branchDistribution) {
      const yes = metrics.branchDistribution.yes || 0
      const no = metrics.branchDistribution.no || 0
      const total = yes + no

      if (total > 0) {
        baseMetrics.push({
          label: 'Yes',
          value: String(yes)
        })
        baseMetrics.push({
          label: 'No',
          value: String(no)
        })
      }
    }

    // Handle SG nodes with split distribution that are pure routing nodes (not questions)
    // These are SG nodes with ask=false and split=true
    if (metrics.nodeType === 'sg' && metrics.splitDistribution && 
        (metrics.visitorCount === undefined || metrics.conversionCount === undefined)) {
      // For pure routing SG nodes, we don't show the raw splits data in the UI
      // The SplitNode component handles the display of individual branch metrics
      
      // For nodes that are displayed as YesNo (likely have ask=false and split=true),
      // provide Yes/No metrics for compatibility
      let yesCount = 0
      let noCount = 0
      
      // Aggregate all non-fallback segments as "Yes" and fallback as "No"
      for (const [segmentId, count] of Object.entries(metrics.splitDistribution)) {
        if (segmentId === 'fallback') {
          noCount += count as number
        } else {
          yesCount += count as number
        }
      }

      const total = yesCount + noCount
      if (total > 0) {
        baseMetrics.push({
          label: 'Yes',
          value: String(yesCount)
        })
        baseMetrics.push({
          label: 'No', 
          value: String(noCount)
        })
      }
    }
    
    // Handle random nodes with bucket distribution
    if (metrics.nodeType === 'random' && metrics.bucketDistribution) {
      // For random nodes, we don't show the raw bucket data in the UI
      // The node component handles the display of individual bucket metrics
    }

    return baseMetrics
  }

  // Fetch metrics for multiple flows
  const fetchMetricsForFlows = async (flowIds: string[]) => {
    const promises = flowIds.map((flowId) => {
      const query = flowQueries.value[flowId]
      if (query) {
        return query.refetch()
      }
      return Promise.resolve()
    })

    await Promise.all(promises)
  }

  // Get path flow data for a specific flow
  const getPathFlowData = (flowId: string) => {
    // First check cache
    const cached = pathFlowCache.value[flowId]
    
    // Then check if query has fresher data
    const query = flowQueries.value[flowId]
    const queryData = query?.data?.value?.pathFlows
    
    // Return query data if available (fresher), otherwise cache
    return queryData || cached || null
  }

  // Store for batch query
  const batchQuery = ref<any>(null)

  const summaryFlowIds = ref<string[]>([])

  const summaryQuery = ref(
    useQuery({
      queryKey: computed(() => [
        'flowSummaryMetrics',
        currentTeamPid.value,
        summaryFlowIds.value,
        dateRange.value
      ]),
      queryFn: async () => {
        const data = await fetchFlowSummaryMetrics(currentTeamPid.value!, {
          flowIds: summaryFlowIds.value,
          start: dateRange.value.start,
          end: dateRange.value.end
        })
        return data
      },
      enabled: computed(() => !!currentTeamPid.value && summaryFlowIds.value.length > 0),
      staleTime: 30000, // 30 seconds
      gcTime: 300000 // 5 minutes
    })
  )

  // Initialize batch query for multiple flows
  const initBatchFlowMetricsQuery = (flowIds: string[]) => {
    if (!batchQuery.value) {
      batchQuery.value = useQuery({
        queryKey: computed(() => [
          'batchFlowMetrics',
          currentTeamPid.value,
          flowIds,
          dateRange.value
        ]),
        queryFn: async () => {
          const data = await fetchBatchFlowExecutionMetrics(currentTeamPid.value!, {
            flowIds,
            start: dateRange.value.start,
            end: dateRange.value.end
          })

          // Update the caches when batch data is fetched
          if (data.flows) {
            for (const [flowId, metrics] of Object.entries(data.flows)) {
              if (metrics.nodeMetrics) {
                nodeMetricsCache.value[flowId] = metrics.nodeMetrics
              }
              if (metrics.pathFlows) {
                pathFlowCache.value[flowId] = metrics.pathFlows
              }
            }
          }

          return data
        },
        enabled: computed(() => !!currentTeamPid.value && flowIds.length > 0),
        staleTime: 30000, // 30 seconds
        gcTime: 300000 // 5 minutes
      })
    }
    return batchQuery.value
  }

  // Initialize summary query for multiple flows (lightweight, no node metrics)
  const initFlowSummaryQuery = (flowIds: string[]) => {
    const nextFlowIds = Array.from(new Set(flowIds))
    const idsChanged =
      nextFlowIds.length !== summaryFlowIds.value.length ||
      nextFlowIds.some((flowId, index) => summaryFlowIds.value[index] !== flowId)

    if (idsChanged) {
      summaryFlowIds.value = nextFlowIds
    }

    return summaryQuery.value
  }

  // Get metrics from batch query for a specific flow
  const getMetricsFromBatch = (flowId: string): FlowExecutionMetricsResponse | null => {
    const data = unref(batchQuery.value?.data)
    return data?.flows?.[flowId] || null
  }

  // Get summary metrics for a specific flow
  const getSummaryMetrics = (flowId: string): FlowSummaryMetrics | null => {
    const data = unref(summaryQuery.value?.data)
    return data?.flows?.[flowId] || null
  }

  return {
    nodeMetricsCache: readonly(nodeMetricsCache),
    pathFlowCache: readonly(pathFlowCache),
    getNodeMetrics,
    formatNodeMetrics,
    fetchMetricsForFlows,
    initFlowMetricsQuery,
    flowQueries: readonly(flowQueries),
    getPathFlowData,
    initBatchFlowMetricsQuery,
    batchQuery: readonly(batchQuery),
    getMetricsFromBatch,
    initFlowSummaryQuery,
    summaryQuery: readonly(summaryQuery),
    getSummaryMetrics
  }
})
